import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Copy, ArrowLeft, MailCheck, Loader2 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_f59c783b-4061-4c90-8631-f537af31e122/artifacts/nm25t9qa_Calcutta%20Logo.png";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-request", { email: email.toLowerCase() });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-10" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="text-sm text-slate-600 hover:text-[#B33A3A] flex items-center gap-1 mb-10">
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO} alt="Calcutta" className="w-11 h-11 rounded-sm" />
          <div>
            <div className="font-serif text-2xl font-semibold tracking-tight">Calcutta</div>
            <div className="eyebrow text-[10px]">Global IP Management</div>
          </div>
        </div>

        {!result ? (
          <>
            <h1 className="font-serif text-4xl font-semibold tracking-tight leading-tight">
              Forgot your password?
            </h1>
            <p className="text-slate-600 mt-3 text-sm">
              Enter the email on your Calcutta account and we'll issue a one-time magic link that signs you in instantly — no password needed.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="magic-request-form">
              <div>
                <label className="eyebrow block mb-1.5">Email</label>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="magic-email-input"
                  className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="magic-request-btn"
                className="w-full bengal-btn-primary text-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Sparkles size={14} /> {loading ? "Issuing link…" : "Send magic link"}
              </button>
            </form>
            <div className="mt-6 text-xs text-slate-500">
              Remember it after all?{" "}
              <Link to="/login" className="text-[#B33A3A] hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </>
        ) : (
          <div data-testid="magic-request-result">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-[#FAD4D4] text-[#7A1F2B] mb-5">
              <MailCheck size={22} />
            </div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Magic link ready.</h1>
            <p className="text-slate-600 mt-3 text-sm">{result.message}</p>

            {result.magic_url ? (
              <div className="mt-6 bengal-card p-4">
                <div className="eyebrow mb-2">Your one-time link</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm font-mono break-all">
                    {result.magic_url}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(result.magic_url);
                      toast.success("Copied");
                    }}
                    data-testid="copy-magic-btn"
                    className="p-2 border border-slate-300 rounded-sm hover:border-[#B33A3A] hover:text-[#B33A3A]"
                    title="Copy"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <a
                  href={result.magic_url}
                  data-testid="open-magic-link"
                  className="mt-4 w-full bengal-btn-primary text-sm inline-flex items-center justify-center gap-2"
                >
                  <Sparkles size={14} /> Open magic link & sign in
                </a>
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                  In production this link is delivered by email. For the demo environment we show it directly so you can copy or open it.
                </p>
              </div>
            ) : (
              <div className="mt-6 text-sm text-slate-600">
                Check your email — if an account exists, a link has been sent.
              </div>
            )}

            <button
              onClick={() => { setResult(null); setEmail(""); }}
              className="mt-6 text-xs text-slate-500 hover:text-[#B33A3A]"
              data-testid="request-another-btn"
            >
              Request another link →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function MagicConsume() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("pending"); // pending | error
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-consume", { token });
        setUser(data);
        toast.success(`Signed in as ${data.name || data.email}`);
        nav("/dashboard", { replace: true });
      } catch (e) {
        setStatus("error");
        setErr(formatApiError(e));
      }
    })();
    // eslint-disable-next-line
  }, [token]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="magic-consume-page">
      <div className="text-center max-w-sm">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-[#B33A3A] text-white mb-5">
          {status === "pending" ? <Loader2 size={24} className="animate-spin" /> : <Sparkles size={24} />}
        </div>
        {status === "pending" ? (
          <>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Signing you in…</h1>
            <p className="text-slate-500 text-sm mt-3">Verifying your magic link.</p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Link didn't work.</h1>
            <p className="text-slate-500 text-sm mt-3">{err}</p>
            <Link to="/forgot-password" className="mt-5 inline-block text-sm text-[#B33A3A] hover:underline font-medium">
              Request a new magic link →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
